/**
 * useFeatureSelection.ts
 * Feature selection logic for map interactions
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { GeoJSONSource, Map } from "maplibre-gl";
import type { Feature, Geometry } from "geojson";

import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE,
  POLYGON_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import { UNIQUE_ID_PROPERTY } from "@/constants/map";
import { setSelectedFeature } from "@/store/slice/mapSlice";
import { SelectedFeature } from "@/types/map";

export const useFeatureSelection = (map: Map) => {
  const dispatch = useDispatch();

  /**
   * Resolves the dataset's grid source id. `getStyle()` serializes every source
   * and layer, so it is only used for the suffixed-source fallback (site
   * profiling appends `-polygon`). `-centroid` holds label/rank points, not grids.
   */
  const resolveGridSourceId = useCallback(
    (sourceId: string): string | null => {
      if (map.getSource(sourceId)) return sourceId;

      // `getStyle()` is undefined while a style is (re)loading — see waitForSource.
      const matched = Object.keys(map.getStyle()?.sources ?? {}).find(
        (id) => id.startsWith(sourceId) && !id.endsWith("-centroid")
      );

      return matched ?? null;
    },
    [map]
  );

  /**
   * Features from the source's own GeoJSON, not `querySourceFeatures()` — that
   * only sees loaded tiles (so off-screen grids were missed) and repeats features
   * across tile boundaries. MapLibre applies feature state to tiles as they load,
   * so reading the full set once is enough.
   */
  const getSourceFeatures = useCallback(
    (matchedSourceId: string): Feature<Geometry>[] | null => {
      const source = map.getSource<GeoJSONSource>(matchedSourceId);
      const data = source?._data;

      if (!data || typeof data === "string" || !("features" in data))
        return null;

      return data.features as Feature<Geometry>[];
    },
    [map]
  );

  const handleClearSelectedState = useCallback(
    (currentSelectedFeature: SelectedFeature) => {
      if (!map) return;

      if (currentSelectedFeature?.id === 0 || !!currentSelectedFeature?.id) {
        if (map.getSource(currentSelectedFeature.source)) {
          map.setFeatureState(
            {
              source: currentSelectedFeature.source,
              id: currentSelectedFeature.id
            },
            { selected: false }
          );
        }
        dispatch(setSelectedFeature(null));
      }
    },
    [map, dispatch]
  );

  /**
   * Highlight multiple grids by their Grid ID using Maplibre's feature state.
   *
   * Deselects with one `removeFeatureState({ source })`, which only touches
   * features that carry state. The writes below still win: `SourceFeatureState`
   * is order-agnostic within a frame.
   *
   * @param sourceId - The ID of the data source in the map.
   * @param gridIds - An array of "Grid ID" values to highlight.
   */
  const handleHighlightGridSelection = useCallback(
    (sourceId: string, gridIds: string[]) => {
      if (!map || !sourceId) return;

      const matchedSourceId = resolveGridSourceId(sourceId);
      if (!matchedSourceId) return;

      const features = getSourceFeatures(matchedSourceId);
      if (!features) return;

      // Set, not `gridIds.includes()`: that was O(features × selection).
      const targetGridIds = new Set(gridIds);

      map.removeFeatureState({ source: matchedSourceId });

      for (const feature of features) {
        const properties = feature.properties || {};
        const featureId = properties[UNIQUE_ID_PROPERTY];
        if (featureId == null) continue;

        const id =
          properties[GRID_ID_ATTRIBUTE] ??
          properties[CATCHMENT_ID_ATTRIBUTE] ??
          properties[POLYGON_ID_ATTRIBUTE];

        if (!targetGridIds.has(id)) continue;

        map.setFeatureState(
          { source: matchedSourceId, id: featureId },
          { selected: true }
        );
      }
    },
    [map, resolveGridSourceId, getSourceFeatures]
  );

  const handleClearHighlightedGrids = useCallback(
    (sourceId: string | null) => {
      if (!map || !sourceId) return;

      const matchedSourceId = resolveGridSourceId(sourceId);
      if (!matchedSourceId) return;

      map.removeFeatureState({ source: matchedSourceId });
    },
    [map, resolveGridSourceId]
  );

  return {
    handleClearSelectedState,
    handleHighlightGridSelection,
    handleClearHighlightedGrids
  };
};

export default useFeatureSelection;
